import { serializeToQueryString } from "../../utils/queryStringSerialization"
import { AllCompaniesQuerystring } from "../../../../backend/src/routes/company/all.types"
import {
    usersFilterSerializationMapper,
    supplierFilterSerializationMapper,
    companyFilterSerializationMapper,
    paymentRequestsFilterSerializationMapper,
    invoicesFilterSerializationMapper,
    archiveFilterSerializationMapper,
} from "../serialization"
import { AllUsersQuerystring } from "../../../../backend/src/routes/user/all.types"
import { AllSuppliersQuerystring } from "../../../../backend/src/routes/supplier/all.types"
import { InvoicesListQuerystring } from "../../components/invoice/list/types"
import { UseFormTabs } from "../../components/user/Form/types"
import { CompanyFormTabs } from "../../components/company/Form/types"
import { PaymentRequestsListQuerystring } from "../../components/payment-request/List/types"
import { ArchiveListQuerystring } from "../../components/archive/List/types"
import { AppAdminRoutesBase, AppDynamicRoute, AppGuestRoutesBase, AppRoute } from "./base"

export type GuestRoutesDict = {
    landing: AppRoute
    login: AppRoute
    passwordRecover: AppRoute & {
        children: {
            verify: AppDynamicRoute<string>
        }
    }
}

export type AdminRoutesDict = {
    system: {
        version: AppDynamicRoute<string>
        status: AppRoute
    }
    dashboard: AppRoute
    companies: AppDynamicRoute<AllCompaniesQuerystring> & {
        children: {
            new: AppDynamicRoute<AllCompaniesQuerystring>
            edit: AppDynamicRoute<number, AllCompaniesQuerystring, CompanyFormTabs>
        }
    }
    suppliers: AppDynamicRoute<AllSuppliersQuerystring> & {
        children: {
            new: AppDynamicRoute<AllSuppliersQuerystring>
            edit: AppDynamicRoute<number, AllSuppliersQuerystring>
        }
    }
    users: AppDynamicRoute<AllUsersQuerystring> & {
        children: {
            new: AppDynamicRoute<AllUsersQuerystring>
            edit: AppDynamicRoute<number, AllUsersQuerystring, UseFormTabs>
        }
    }
    paymentRequest: AppDynamicRoute<PaymentRequestsListQuerystring> & {
        children: {
            new: AppDynamicRoute<PaymentRequestsListQuerystring>
            edit: AppDynamicRoute<number, PaymentRequestsListQuerystring>
        }
    }
    invoices: AppDynamicRoute<InvoicesListQuerystring> & {
        children: {
            new: AppDynamicRoute<InvoicesListQuerystring>
            detail: AppDynamicRoute<number, InvoicesListQuerystring>
        }
    }
    invoicePreview: AppDynamicRoute<number>
    archive: AppDynamicRoute<ArchiveListQuerystring> & {
        children: {
            upload: AppDynamicRoute<ArchiveListQuerystring>
        }
    }
    utils: {
        invoicesExport: AppRoute
    }
}

export const AppGuestRoutes: GuestRoutesDict = {
    landing: {
        path: "/",
        title: () => "Homepage",
        getLink: () => "/",
    },
    login: {
        path: "login",
        title: () => "Login",
        getLink: () => AppGuestRoutesBase.login.getLink(),
    },
    passwordRecover: {
        path: "password-recover",
        title: () => "Password recover",
        getLink: () => AppGuestRoutesBase.passwordRecover.getLink(),
        children: {
            verify: {
                path: ":token",
                title: () => "Password verify",
                getLink: (token?: string) => AppGuestRoutes.passwordRecover.children.verify.getLink(token),
            },
        },
    },
}

export const AppAdminRoutes: AdminRoutesDict = {
    system: {
        version: {
            path: "version/:version",
            title: () => "Verze aplikace",
            getLink: (version?: string) => AppAdminRoutesBase.system.version.getLink(version),
        },
        status: {
            path: "status",
            title: () => "Status aplikace",
            getLink: () => AppAdminRoutesBase.system.status.getLink(),
        },
    },
    dashboard: {
        path: "/",
        title: () => "Dashboard",
        getLink: () => AppAdminRoutesBase.dashboard.getLink(),
    },
    companies: {
        path: "companies",
        title: () => "Companies",
        getLink: (args) =>
            AppAdminRoutesBase.companies.getLink() +
            (args ? "?" + serializeToQueryString(args, companyFilterSerializationMapper) : ""),
        children: {
            new: {
                path: "new",
                title: () => "New company",
                getLink: (args) =>
                    AppAdminRoutesBase.companies.children.new.getLink() +
                    (args ? "?" + serializeToQueryString(args, companyFilterSerializationMapper) : ""),
            },
            edit: {
                path: ":companyId",
                title: () => "Edit company",
                getLink: (id, args, tab) =>
                    AppAdminRoutesBase.companies.children.edit.getLink(id) +
                    (tab ? `#${tab}` : "") +
                    (args ? "?" + serializeToQueryString(args, companyFilterSerializationMapper) : ""),
            },
        },
    },
    suppliers: {
        path: "suppliers",
        title: () => "Suppliers",
        getLink: (args) =>
            AppAdminRoutesBase.suppliers.getLink() +
            (args ? "?" + serializeToQueryString(args, supplierFilterSerializationMapper) : ""),
        children: {
            new: {
                path: "new",
                title: () => "New supplier",
                getLink: (args) =>
                    AppAdminRoutesBase.suppliers.children.new.getLink() +
                    (args ? "?" + serializeToQueryString(args, supplierFilterSerializationMapper) : ""),
            },
            edit: {
                path: ":supplierId",
                title: () => "Edit supplier",
                getLink: (id, args) =>
                    AppAdminRoutesBase.suppliers.children.edit.getLink(id) +
                    (args ? "?" + serializeToQueryString(args, supplierFilterSerializationMapper) : ""),
            },
        },
    },
    users: {
        path: "users",
        title: () => "Users",
        getLink: (args) => "/users" + (args ? "?" + serializeToQueryString(args, usersFilterSerializationMapper) : ""),
        children: {
            new: {
                path: "new",
                title: () => "New user",
                getLink: (args) =>
                    AppAdminRoutesBase.users.children.new.getLink() +
                    (args ? "?" + serializeToQueryString(args, usersFilterSerializationMapper) : ""),
            },
            edit: {
                path: ":userId",
                title: () => "Edit user",
                getLink: (id, args, tab) =>
                    AppAdminRoutesBase.users.children.edit.getLink(id) +
                    (tab ? `#${tab}` : "") +
                    (args ? "?" + serializeToQueryString(args, usersFilterSerializationMapper) : ""),
            },
        },
    },
    paymentRequest: {
        path: "payment-requests",
        title: () => "Payment requests",
        getLink: (args) =>
            AppAdminRoutesBase.paymentRequest.getLink() +
            (args ? "?" + serializeToQueryString(args, paymentRequestsFilterSerializationMapper) : ""),
        children: {
            new: {
                path: "new",
                title: () => "New payment request",
                getLink: (args) =>
                    AppAdminRoutesBase.paymentRequest.children.new.getLink() +
                    (args ? "?" + serializeToQueryString(args, paymentRequestsFilterSerializationMapper) : ""),
            },
            edit: {
                path: ":requestId",
                title: () => "Edit payment request",
                getLink: (id, args) =>
                    AppAdminRoutesBase.paymentRequest.children.edit.getLink(id) +
                    (args ? "?" + serializeToQueryString(args, paymentRequestsFilterSerializationMapper) : ""),
            },
        },
    },
    invoices: {
        path: "invoices",
        title: () => "Invoices",
        getLink: (args) =>
            AppAdminRoutesBase.invoices.getLink() +
            (args ? "?" + serializeToQueryString(args, invoicesFilterSerializationMapper) : ""),
        children: {
            new: {
                path: "new",
                title: () => "New invoice",
                getLink: (args) =>
                    AppAdminRoutesBase.invoices.children.new.getLink() +
                    (args ? "?" + serializeToQueryString(args, invoicesFilterSerializationMapper) : ""),
            },
            detail: {
                path: ":invoiceId",
                title: () => "Invoice detail",
                getLink: (id, args) =>
                    AppAdminRoutesBase.invoices.children.detail.getLink(id) +
                    (args ? "?" + serializeToQueryString(args, invoicesFilterSerializationMapper) : ""),
            },
        },
    },
    invoicePreview: {
        path: "invoice/:invoiceId/preview",
        title: () => "Invoice preview",
        getLink: (id) => AppAdminRoutesBase.invoicePreview.getLink(id),
    },
    archive: {
        path: "archive",
        title: () => "Archive",
        getLink: (args) =>
            AppAdminRoutesBase.archive.getLink() +
            (args ? "?" + serializeToQueryString(args, archiveFilterSerializationMapper) : ""),
        children: {
            upload: {
                path: "upload",
                title: () => "Upload",
                getLink: (args) =>
                    AppAdminRoutesBase.archive.children.upload.getLink() +
                    (args ? "?" + serializeToQueryString(args, archiveFilterSerializationMapper) : ""),
            },
        },
    },
    utils: {
        invoicesExport: {
            path: "invoices-export",
            title: () => "Invoices export",
            getLink: () => "/invoices-export",
        },
    },
}
